import { useEffect, useState } from "react";
import { isStringEmpty } from "../utils/handler_validations";
import { whitelistDB, driverDB, passengerDB } from "../services";
import { useSessionContext } from "../context/session.context";
import { useVeryNotificationsContext } from "../components/common/v2/VeryNotifications/useVeryNotifications";
import { updateAppStatus } from "../services/appStatus";

const getLoadErrorTemplate = (initialLoading = false) => ({
  loading: initialLoading === true,
  error: { message: "", details: "" },
});

const toRowItem = (row, index) => ({
  id: index,
  item: {
    id: row.id,
    tel: row.tel,
    name: row.name || "",
    userType: row.userType,
    createdAt: row.createdAt,
  },
});

export const useWhitelist = () => {
  const [value, setValue] = useState({
    page: getLoadErrorTemplate(true),
    rawList: [],
    enabled: false,
    shipmentsRawList: [],
    shipmentsEnabled: false,
  });
  const [textInputSearch, setTextInputSearch] = useState("");
  const [blockedMessage, setBlockedMessage] = useState("");
  const [enabledLocal, setEnabledLocal] = useState(false);
  const [pickerType, setPickerType] = useState("DRIVER");
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerResults, setPickerResults] = useState({
    list: [],
    loading: false,
  });
  const [pickerAddType, setPickerAddType] = useState("DRIVER");
  const [addingTel, setAddingTel] = useState(null);
  const [mainTab, setMainTab] = useState("WHITELIST"); // "WHITELIST", "SHIPMENTS", "SANDBOX"
  const [testersList, setTestersList] = useState([]);
  const [loadingTesters, setLoadingTesters] = useState(false);

  // Shipments state
  const [shipmentsSearchText, setShipmentsSearchText] = useState("");
  const [shipmentsEnabledLocal, setShipmentsEnabledLocal] = useState(false);
  const [allowShipmentsGlobal, setAllowShipmentsGlobal] = useState(true);
  const [shipmentsDisabledMessage, setShipmentsDisabledMessage] = useState("El servicio de envíos se encuentra temporalmente deshabilitado.");
  const [savingShipmentsConfig, setSavingShipmentsConfig] = useState(false);

  const session = useSessionContext();
  const notis = useVeryNotificationsContext();

  useEffect(() => {
    getListInitial();
    getTestersInitial();
  }, []);

  const applyFilter = (rawList = [], search = "") => {
    const base = isStringEmpty(search)
      ? rawList
      : rawList.filter((row) => row.tel.includes(search.trim()));
    return base.map(toRowItem);
  };

  const getListInitial = async () => {
    setValue((prev) => ({ ...prev, page: getLoadErrorTemplate(true) }));
    const resp = await whitelistDB.getWhitelist(session.value.jwt);
    if (resp.error) {
      setValue((prev) => ({
        ...prev,
        page: {
          loading: false,
          error: { message: resp.error.message, details: resp.error.details },
        },
      }));
      return;
    }
    const rawList = resp.body?.whitelist ?? [];
    const shipmentsRawList = resp.body?.shipmentsWhitelist ?? [];
    const shipmentsEnabled = resp.body?.shipmentsWhitelistEnabled ?? false;
    setValue((prev) => ({
      ...prev,
      page: getLoadErrorTemplate(false),
      rawList,
      enabled: resp.body?.enabled ?? false,
      shipmentsRawList,
      shipmentsEnabled,
    }));
    setEnabledLocal(resp.body?.enabled ?? false);
    setBlockedMessage(resp.body?.blockedMessage ?? "");
    setShipmentsEnabledLocal(shipmentsEnabled);
    setTextInputSearch("");

    // Load global shipments config from appStatus
    const appStatusResp = await updateAppStatus({}, session.value.jwt).catch(() => ({}));
    if (appStatusResp?.body) {
      setAllowShipmentsGlobal(appStatusResp.body.allowShipments !== false);
      if (appStatusResp.body.shipmentsDisabledMessage) {
        setShipmentsDisabledMessage(appStatusResp.body.shipmentsDisabledMessage);
      }
    }
  };

  const onChangeTextInputSearch = (newText = "") => {
    setTextInputSearch(newText);
  };

  const onChangePickerType = (type = "DRIVER") => {
    setPickerType(type);
    setPickerAddType(type);
    setPickerSearch("");
    setPickerResults({ list: [], loading: false });
  };

  const searchPickerUsers = async (searchText = pickerSearch) => {
    if (isStringEmpty(searchText)) return;
    setPickerResults({ list: [], loading: true });
    const resp =
      pickerType === "DRIVER"
        ? await driverDB.getListDrivers(searchText, session.value.jwt)
        : await passengerDB.getListPassengers(searchText, session.value.jwt);
    if (resp.error) {
      setPickerResults({ list: [], loading: false });
      notis.createNotiError("Error al buscar usuarios", resp.error.message);
      return;
    }
    const list =
      pickerType === "DRIVER"
        ? (resp.body?.drivers ?? []).map((d) => ({
            id: d.id,
            tel: d.tel,
            name: `${d.name} ${d.lastName}`.trim(),
          }))
        : (resp.body?.passengers ?? []).map((p) => ({
            id: p.id,
            tel: p.tel,
            name: p.fullName,
          }));
    setPickerResults({ list, loading: false });
  };

  const execAddFromPicker = async (tel = "", feature = "APP") => {
    if (isStringEmpty(tel)) return;
    setAddingTel(tel);
    const resp = await whitelistDB.addToWhitelist(
      tel,
      pickerAddType,
      session.value.jwt,
      feature
    );
    setAddingTel(null);
    if (resp.error) {
      notis.createNotiError(resp.error.message, resp.error.details);
      return;
    }
    const displayTel = tel.startsWith("+52") ? tel.slice(3) : tel;
    notis.createNotiSuccess(
      "Registro agregado",
      `Teléfono ${displayTel} agregado a la lista ${feature === "SHIPMENTS" ? "de envíos" : "de acceso general"}`,
    );
    getListInitial();
  };

  const execDeleteFromWhitelist = async (id = 0, tel = "", feature = "APP") => {
    const resp = await whitelistDB.removeFromWhitelist(id, session.value.jwt, feature);
    if (resp.error) {
      notis.createNotiError(resp.error.message, resp.error.details);
      return;
    }
    notis.createNotiSuccess(
      "Registro eliminado",
      `Teléfono ${tel} eliminado de la lista`,
    );
    if (feature === "SHIPMENTS") {
      setValue((prev) => ({
        ...prev,
        shipmentsRawList: prev.shipmentsRawList.filter((row) => row.id !== id),
      }));
    } else {
      setValue((prev) => ({
        ...prev,
        rawList: prev.rawList.filter((row) => row.id !== id),
      }));
    }
  };

  const saveWhitelistConfig = async () => {
    setValue((prev) => ({ ...prev, page: getLoadErrorTemplate(true) }));
    const [respMsg, respToggle] = await Promise.all([
      updateAppStatus({ whitelistBlockedMessage: blockedMessage }, session.value.jwt),
      whitelistDB.toggleWhitelist(enabledLocal, session.value.jwt, "APP"),
    ]);
    if (respMsg.error || respToggle.error) {
      setValue((prev) => ({ ...prev, page: getLoadErrorTemplate(false) }));
      const err = respMsg.error || respToggle.error;
      notis.createNotiError("Error al guardar cambios", err.message);
      return;
    }
    setValue((prev) => ({
      ...prev,
      page: getLoadErrorTemplate(false),
      enabled: enabledLocal,
    }));
    notis.createNotiSuccess("Cambios guardados", "La configuración de la whitelist fue actualizada.");
  };

  const saveShipmentsConfig = async () => {
    setSavingShipmentsConfig(true);
    const [respStatus, respToggle] = await Promise.all([
      updateAppStatus(
        {
          allowShipments: allowShipmentsGlobal,
          shipmentsDisabledMessage,
        },
        session.value.jwt
      ),
      whitelistDB.toggleWhitelist(shipmentsEnabledLocal, session.value.jwt, "SHIPMENTS"),
    ]);
    setSavingShipmentsConfig(false);
    if (respStatus.error || respToggle.error) {
      const err = respStatus.error || respToggle.error;
      notis.createNotiError("Error al guardar cambios de envíos", err.message);
      return;
    }
    setValue((prev) => ({
      ...prev,
      shipmentsEnabled: shipmentsEnabledLocal,
    }));
    notis.createNotiSuccess(
      "Configuración de Envíos Guardada",
      "El estado global y el modo lista de envíos fueron actualizados correctamente."
    );
  };

  const getTestersInitial = async () => {
    setLoadingTesters(true);
    const resp = await passengerDB.getTestersList(session.value.jwt);
    setLoadingTesters(false);
    if (resp.error) {
      notis.createNotiError("Error al cargar testers", resp.error.message);
      return;
    }
    setTestersList(resp.body?.list ?? []);
  };

  const execAddTesterFromPicker = async (userId = 0, tel = "") => {
    if (!userId) return;
    setAddingTel(tel);
    const resp = await passengerDB.toggleUserTester(userId, 1, session.value.jwt); // 1 = Sandbox Activo
    setAddingTel(null);
    if (resp.error) {
      notis.createNotiError("Error al aislar usuario", resp.error.message);
      return;
    }
    notis.createNotiSuccess(
      "Usuario aislado",
      `Usuario ${tel} agregado al Sandbox de pruebas`
    );
    getTestersInitial();
  };

  const execRemoveTester = async (userId = 0, tel = "") => {
    if (!userId) return;
    const resp = await passengerDB.toggleUserTester(userId, 0, session.value.jwt); // 0 = Eliminar / Reset a normal
    if (resp.error) {
      notis.createNotiError("Error al retirar usuario", resp.error.message);
      return;
    }
    notis.createNotiSuccess(
      "Usuario liberado",
      `Usuario ${tel} retirado del Sandbox y devuelto al flujo real`
    );
    getTestersInitial();
  };

  const execToggleUserTesterState = async (userId = 0, isActive = false, tel = "") => {
    if (!userId) return;
    const newState = isActive ? 1 : 2; // 1 = Sandbox Activo, 2 = Sandbox Inactivo
    const resp = await passengerDB.toggleUserTester(userId, newState, session.value.jwt);
    if (resp.error) {
      notis.createNotiError("Error al cambiar estado de Sandbox", resp.error.message);
      return;
    }
    notis.createNotiSuccess(
      isActive ? "Sandbox Activo" : "Sandbox Desactivado",
      `Usuario ${tel} ahora está en flujo ${isActive ? "AISLADO (Tester)" : "REAL (Normal)"}`
    );
    getTestersInitial();
  };

  const filteredList = applyFilter(value.rawList, textInputSearch);
  const filteredShipmentsList = applyFilter(value.shipmentsRawList, shipmentsSearchText);

  return {
    value,
    filteredList,
    filteredShipmentsList,
    textInputSearch,
    shipmentsSearchText,
    setShipmentsSearchText,
    pickerType,
    pickerSearch,
    pickerResults,
    pickerAddType,
    addingTel,
    getListInitial,
    onChangeTextInputSearch,
    onChangePickerType,
    setPickerSearch,
    setPickerAddType,
    searchPickerUsers,
    execAddFromPicker,
    execDeleteFromWhitelist,
    blockedMessage,
    setBlockedMessage,
    enabledLocal,
    setEnabledLocal,
    saveWhitelistConfig,
    // Shipments state & methods
    shipmentsEnabledLocal,
    setShipmentsEnabledLocal,
    allowShipmentsGlobal,
    setAllowShipmentsGlobal,
    shipmentsDisabledMessage,
    setShipmentsDisabledMessage,
    savingShipmentsConfig,
    saveShipmentsConfig,
    // Sandbox states & methods
    mainTab,
    setMainTab,
    testersList,
    loadingTesters,
    getTestersInitial,
    execAddTesterFromPicker,
    execRemoveTester,
    execToggleUserTesterState,
  };
};
